import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrg } from '../contexts/OrgContext'
import { organizationsAPI, usersAPI, rolesAPI } from '../api/client'

export default function SystemAdminPage() {
  const navigate = useNavigate()
  const { isSuperAdmin } = useOrg()

  // Organizations state
  const [organizations, setOrganizations] = useState([])
  const [loadingOrgs, setLoadingOrgs] = useState(true)

  // Portal Admins state
  const [portalAdmins, setPortalAdmins] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [loadingAdmins, setLoadingAdmins] = useState(true)

  // Roles state
  const [roles, setRoles] = useState([])
  const [loadingRoles, setLoadingRoles] = useState(true)

  // UI state
  const [activeTab, setActiveTab] = useState('organizations')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Modal state
  const [showOrgModal, setShowOrgModal] = useState(false)
  const [showGrantAdminModal, setShowGrantAdminModal] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false)
  const [editingOrg, setEditingOrg] = useState(null)
  const [editingRole, setEditingRole] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  // Form state
  const [orgForm, setOrgForm] = useState({ name: '', slug: '', settings: {} })
  const [selectedUserId, setSelectedUserId] = useState('')
  const [roleForm, setRoleForm] = useState({ name: '', description: '', capabilities: [] })

  // Capability configuration
  const CAPABILITY_GROUPS = {
    events: { label: 'Events', description: 'Scan events and access logs', actions: ['read', 'delete'] },
    clients: { label: 'Clients', description: 'Client records and profiles', actions: ['read', 'create', 'update', 'delete'] },
    cards: { label: 'Cards', description: 'RFID cards and assignments', actions: ['read', 'update', 'delete'] },
    devices: { label: 'Devices', description: 'RFID terminals and access points', actions: ['read', 'create', 'update', 'delete'] },
    users: { label: 'Users', description: 'User accounts and authentication', actions: ['read', 'create', 'update', 'delete'] },
    roles: { label: 'Roles', description: 'User roles and permissions', actions: ['read', 'create', 'update', 'delete'] }
  }

  const ACTION_LABELS = {
    read: 'View',
    create: 'Create',
    update: 'Edit',
    delete: 'Delete'
  }

  // Redirect if not super admin
  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard')
    }
  }, [isSuperAdmin, navigate])

  // Load data
  useEffect(() => {
    loadOrganizations()
    loadPortalAdmins()
    loadRoles()
  }, [])

  async function loadOrganizations() {
    try {
      setLoadingOrgs(true)
      const response = await organizationsAPI.getAll()
      setOrganizations(response.data?.organizations || [])
    } catch (err) {
      console.error('Failed to load organizations:', err)
      setError('Failed to load organizations')
    } finally {
      setLoadingOrgs(false)
    }
  }

  async function loadPortalAdmins() {
    try {
      setLoadingAdmins(true)
      const response = await usersAPI.getAll()
      const users = response.data?.users || []
      setAllUsers(users)
      const admins = users.filter(user => user.is_super_admin)
      setPortalAdmins(admins)
    } catch (err) {
      console.error('Failed to load portal admins:', err)
      setError('Failed to load portal administrators')
    } finally {
      setLoadingAdmins(false)
    }
  }

  async function loadRoles() {
    try {
      setLoadingRoles(true)
      const response = await rolesAPI.getAll()
      setRoles(response.data?.roles || [])
    } catch (err) {
      console.error('Failed to load roles:', err)
      setError('Failed to load roles')
    } finally {
      setLoadingRoles(false)
    }
  }

  // Organization functions
  function openCreateOrgModal() {
    setEditingOrg(null)
    setOrgForm({ name: '', slug: '', settings: {} })
    setShowOrgModal(true)
  }

  function openEditOrgModal(org) {
    setEditingOrg(org)
    setOrgForm({
      name: org.name,
      slug: org.slug,
      settings: org.settings || {}
    })
    setShowOrgModal(true)
  }

  function closeOrgModal() {
    setShowOrgModal(false)
    setEditingOrg(null)
    setOrgForm({ name: '', slug: '', settings: {} })
  }

  async function handleOrgSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      if (editingOrg) {
        await organizationsAPI.update(editingOrg.org_id, orgForm)
        setSuccess('Organization updated successfully')
      } else {
        await organizationsAPI.create(orgForm)
        setSuccess('Organization created successfully')
      }
      await loadOrganizations()
      closeOrgModal()
    } catch (err) {
      setError(err.message || 'Failed to save organization')
    } finally {
      setSubmitting(false)
    }
  }

  // Portal Admin functions
  function openGrantAdminModal() {
    setSelectedUserId('')
    setShowGrantAdminModal(true)
  }

  function closeGrantAdminModal() {
    setShowGrantAdminModal(false)
    setSelectedUserId('')
  }

  async function handleGrantPortalAdmin(e) {
    e.preventDefault()
    if (!selectedUserId) return

    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      await usersAPI.update(selectedUserId, { is_super_admin: true })
      setSuccess('Portal Admin privileges granted successfully')
      await loadPortalAdmins()
      closeGrantAdminModal()
    } catch (err) {
      setError(err.message || 'Failed to grant Portal Admin privileges')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRevokePortalAdmin(user) {
    if (!confirm(`Are you sure you want to revoke Portal Admin privileges for ${user.email}?`)) {
      return
    }

    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      await usersAPI.update(user.user_id, { is_super_admin: false })
      setSuccess(`Portal Admin privileges revoked for ${user.email}`)
      await loadPortalAdmins()
    } catch (err) {
      setError(err.message || 'Failed to revoke Portal Admin privileges')
    } finally {
      setSubmitting(false)
    }
  }

  // Role functions
  function openCreateRoleModal() {
    setEditingRole(null)
    setRoleForm({ name: '', description: '', capabilities: [] })
    setShowRoleModal(true)
  }

  function openEditRoleModal(role) {
    setEditingRole(role)
    setRoleForm({
      name: role.name,
      description: role.description || '',
      capabilities: role.capabilities || []
    })
    setShowRoleModal(true)
  }

  function openViewRoleModal(role) {
    setEditingRole({ ...role, viewOnly: true })
    setRoleForm({
      name: role.name,
      description: role.description || '',
      capabilities: role.capabilities || []
    })
    setShowRoleModal(true)
  }

  function closeRoleModal() {
    setShowRoleModal(false)
    setEditingRole(null)
    setRoleForm({ name: '', description: '', capabilities: [] })
    setError('')
  }

  function openDeleteRoleModal(role) {
    setEditingRole(role)
    setShowDeleteRoleModal(true)
  }

  function closeDeleteRoleModal() {
    setShowDeleteRoleModal(false)
    setEditingRole(null)
    setError('')
  }

  // Capability management functions
  function hasCapability(resource, action) {
    return roleForm.capabilities.includes(`${resource}.${action}`) ||
           roleForm.capabilities.includes(`${resource}.*`) ||
           roleForm.capabilities.includes('*')
  }

  function hasAllResourceCapabilities(resource) {
    return roleForm.capabilities.includes(`${resource}.*`) ||
           roleForm.capabilities.includes('*')
  }

  function toggleCapability(resource, action) {
    const capability = `${resource}.${action}`
    const wildcard = `${resource}.*`

    let newCapabilities = [...roleForm.capabilities]

    // Remove wildcards if they exist (both resource and full wildcards)
    newCapabilities = newCapabilities.filter(cap => cap !== wildcard && cap !== '*')

    if (newCapabilities.includes(capability)) {
      // Remove the capability
      newCapabilities = newCapabilities.filter(cap => cap !== capability)
    } else {
      // Add the capability
      newCapabilities.push(capability)
    }

    setRoleForm({ ...roleForm, capabilities: newCapabilities })
  }

  function toggleResourceWildcard(resource) {
    const wildcard = `${resource}.*`
    let newCapabilities = [...roleForm.capabilities]

    if (hasAllResourceCapabilities(resource)) {
      // Remove wildcard (including full wildcard '*')
      newCapabilities = newCapabilities.filter(cap => cap !== '*' && !cap.startsWith(`${resource}.`))
    } else {
      // Remove individual capabilities and add wildcard
      newCapabilities = newCapabilities.filter(cap => cap !== '*' && !cap.startsWith(`${resource}.`))
      newCapabilities.push(wildcard)
    }

    setRoleForm({ ...roleForm, capabilities: newCapabilities })
  }

  async function handleRoleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      // Validate
      if (!roleForm.name.trim()) {
        setError('Role name is required')
        setSubmitting(false)
        return
      }

      if (roleForm.capabilities.length === 0) {
        setError('At least one capability is required')
        setSubmitting(false)
        return
      }

      const roleData = {
        name: roleForm.name.trim(),
        description: roleForm.description.trim(),
        capabilities: roleForm.capabilities
      }

      if (editingRole) {
        await rolesAPI.update(editingRole.role_id, roleData)
        setSuccess('Role updated successfully')
      } else {
        await rolesAPI.create(roleData)
        setSuccess('Role created successfully')
      }

      await loadRoles()
      closeRoleModal()
    } catch (err) {
      setError(err.message || 'Failed to save role')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDeleteRole() {
    if (!editingRole) return

    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      await rolesAPI.delete(editingRole.role_id)
      setSuccess(`Role "${editingRole.name}" deleted successfully`)
      await loadRoles()
      closeDeleteRoleModal()
    } catch (err) {
      setError(err.message || 'Failed to delete role')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isSuperAdmin) {
    return null
  }

  // Filter out users who are already Portal Admins for the Grant Admin dropdown
  const nonAdminUsers = allUsers.filter(user => !user.is_super_admin)

  return (
    <div className="system-admin-page">
      <div className="page-header">
        <div>
          <h1>System Administration</h1>
          <p>Manage organizations and portal administrators</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '2rem' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '2rem' }}>
          {success}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tabs" style={{ marginBottom: '2rem' }}>
        <button
          className={`tab-button ${activeTab === 'organizations' ? 'active' : ''}`}
          onClick={() => setActiveTab('organizations')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: activeTab === 'organizations' ? 'var(--primary)' : 'var(--surface)',
            color: activeTab === 'organizations' ? '#fff' : 'var(--text)',
            cursor: 'pointer',
            borderRadius: '0.375rem 0.375rem 0 0',
            fontWeight: '500',
            marginRight: '0.5rem'
          }}
        >
          Organizations
        </button>
        <button
          className={`tab-button ${activeTab === 'admins' ? 'active' : ''}`}
          onClick={() => setActiveTab('admins')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: activeTab === 'admins' ? 'var(--primary)' : 'var(--surface)',
            color: activeTab === 'admins' ? '#fff' : 'var(--text)',
            cursor: 'pointer',
            borderRadius: '0.375rem 0.375rem 0 0',
            fontWeight: '500',
            marginRight: '0.5rem'
          }}
        >
          Portal Administrators
        </button>
        <button
          className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
          onClick={() => setActiveTab('roles')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: activeTab === 'roles' ? 'var(--primary)' : 'var(--surface)',
            color: activeTab === 'roles' ? '#fff' : 'var(--text)',
            cursor: 'pointer',
            borderRadius: '0.375rem 0.375rem 0 0',
            fontWeight: '500'
          }}
        >
          Roles
        </button>
      </div>

      {/* Organizations Tab */}
      {activeTab === 'organizations' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2>Organizations</h2>
            <button className="btn btn-primary" onClick={openCreateOrgModal}>
              + Create Organization
            </button>
          </div>

          {loadingOrgs ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : organizations.length === 0 ? (
            <div className="no-data">
              No organizations found. Create your first organization to get started.
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.map(org => (
                    <tr key={org.org_id}>
                      <td style={{ fontWeight: '500' }}>{org.name}</td>
                      <td>
                        <code style={{
                          background: 'var(--background)',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.875rem'
                        }}>
                          {org.slug}
                        </code>
                      </td>
                      <td>
                        <span className={`badge badge-${
                          org.status === 'active' ? 'success' :
                          org.status === 'inactive' ? 'warning' : 'danger'
                        }`}>
                          {org.status}
                        </span>
                      </td>
                      <td>{new Date(org.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          onClick={() => openEditOrgModal(org)}
                          style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Portal Administrators Tab */}
      {activeTab === 'admins' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2>Portal Administrators</h2>
            <button className="btn btn-primary" onClick={openGrantAdminModal}>
              + Grant Portal Admin
            </button>
          </div>

          <div className="alert alert-warning" style={{ marginBottom: '1.5rem' }}>
            <strong>Note:</strong> Portal Administrators have system-wide access to all organizations and can manage all aspects of the portal.
            Grant this privilege carefully.
          </div>

          {loadingAdmins ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : portalAdmins.length === 0 ? (
            <div className="no-data">
              No portal administrators found.
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {portalAdmins.map(admin => (
                    <tr key={admin.user_id}>
                      <td style={{ fontWeight: '500' }}>
                        {admin.first_name} {admin.last_name}
                      </td>
                      <td>{admin.email}</td>
                      <td>
                        <span className={`badge badge-${
                          admin.status === 'active' ? 'success' :
                          admin.status === 'inactive' ? 'warning' : 'danger'
                        }`}>
                          {admin.status}
                        </span>
                      </td>
                      <td>{new Date(admin.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleRevokePortalAdmin(admin)}
                          disabled={submitting}
                          style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                        >
                          Revoke Access
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Emergency Account Info */}
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            background: 'var(--background)',
            borderRadius: '0.5rem',
            border: '1px solid var(--border)'
          }}>
            <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Emergency Access Account</h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', margin: 0 }}>
              Username: <code>portaladmin</code><br />
              Email: <code>portaladmin@system.local</code><br />
              <small>Password can be reset via AWS Cognito CLI. This account always has Portal Admin privileges.</small>
            </p>
          </div>
        </div>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2>Roles</h2>
            <button className="btn btn-primary" onClick={openCreateRoleModal}>
              + Create Role
            </button>
          </div>

          <div className="alert alert-warning" style={{ marginBottom: '1.5rem' }}>
            <strong>Note:</strong> The Admin role is fully protected and cannot be edited. Manager and Viewer system roles can be viewed and edited.
            You can also create custom roles to define specific permission sets for your organization.
          </div>

          {loadingRoles ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : roles.length === 0 ? (
            <div className="no-data">
              No roles found.
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Capabilities</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map(role => {
                    const isSystemRole = role.is_system_role
                    const capabilityCount = role.capabilities?.length || 0
                    const hasWildcard = role.capabilities?.includes('*')

                    return (
                      <tr key={role.role_id}>
                        <td style={{ fontWeight: '500' }}>{role.name}</td>
                        <td style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
                          {role.description || '-'}
                        </td>
                        <td>
                          <span style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                            {hasWildcard ? 'All Permissions' : `${capabilityCount} permission${capabilityCount !== 1 ? 's' : ''}`}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${isSystemRole ? 'badge-info' : 'badge-success'}`}>
                            {isSystemRole ? 'System' : 'Custom'}
                          </span>
                        </td>
                        <td>
                          {isSystemRole ? (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                className="btn btn-secondary"
                                onClick={() => openViewRoleModal(role)}
                                style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                              >
                                View
                              </button>
                              {role.name !== 'Admin' && (
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => openEditRoleModal(role)}
                                  style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                className="btn btn-secondary"
                                onClick={() => openEditRoleModal(role)}
                                style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-danger"
                                onClick={() => openDeleteRoleModal(role)}
                                style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Organization Modal */}
      {showOrgModal && (
        <div className="modal-overlay" onClick={closeOrgModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>{editingOrg ? 'Edit Organization' : 'Create Organization'}</h2>
            <form onSubmit={handleOrgSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={orgForm.name}
                  onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Slug *</label>
                <input
                  type="text"
                  value={orgForm.slug}
                  onChange={(e) => setOrgForm({ ...orgForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="lowercase-with-hyphens"
                  required
                />
                <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--text-light)' }}>
                  Lowercase alphanumeric with hyphens only
                </small>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeOrgModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : (editingOrg ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Grant Portal Admin Modal */}
      {showGrantAdminModal && (
        <div className="modal-overlay" onClick={closeGrantAdminModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>Grant Portal Admin Privileges</h2>
            <form onSubmit={handleGrantPortalAdmin}>
              <div className="form-group">
                <label>Select User *</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  required
                >
                  <option value="">Choose a user...</option>
                  {nonAdminUsers.map(user => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </option>
                  ))}
                </select>
                <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--text-light)' }}>
                  Only users without Portal Admin privileges are shown
                </small>
              </div>

              <div className="alert alert-warning" style={{ marginTop: '1rem' }}>
                <strong>Warning:</strong> This user will have full system access to all organizations and settings.
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeGrantAdminModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting || !selectedUserId}>
                  {submitting ? 'Granting...' : 'Grant Access'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Role Modal */}
      {showRoleModal && (
        <div className="modal-overlay" onClick={closeRoleModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>
              {editingRole?.viewOnly ? 'View Role' : (editingRole ? 'Edit Role' : 'Create Role')}
            </h2>

            {error && (
              <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            {editingRole?.viewOnly && (
              <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
                <strong>View Only:</strong> You are viewing this role's capabilities. {editingRole.name === 'Admin' ? 'The Admin role is fully protected and cannot be edited.' : 'Click Edit to modify this role.'}
              </div>
            )}

            {editingRole?.is_system_role && !editingRole?.viewOnly && editingRole.name === 'Admin' && (
              <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
                <strong>Protected Role:</strong> The Admin role is fully protected and cannot be modified.
              </div>
            )}

            <form onSubmit={handleRoleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  disabled={editingRole?.viewOnly || editingRole?.is_system_role}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  disabled={editingRole?.viewOnly || (editingRole?.is_system_role && editingRole?.name === 'Admin')}
                  rows={2}
                  placeholder="Optional description of this role..."
                />
              </div>

              <div className="form-group">
                <label>Capabilities *</label>
                <div style={{
                  border: '1px solid var(--border)',
                  borderRadius: '0.375rem',
                  padding: '1rem',
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}>
                  {Object.entries(CAPABILITY_GROUPS).map(([resource, config]) => (
                    <div key={resource} style={{
                      marginBottom: '1.5rem',
                      paddingBottom: '1rem',
                      borderBottom: '1px solid var(--border)'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.75rem'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                            {config.label}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
                            {config.description}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleResourceWildcard(resource)}
                          disabled={editingRole?.viewOnly || (editingRole?.is_system_role && editingRole?.name === 'Admin')}
                          className="btn btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                        >
                          {hasAllResourceCapabilities(resource) ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '0.5rem'
                      }}>
                        {config.actions.map(action => (
                          <label
                            key={action}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              cursor: (editingRole?.viewOnly || (editingRole?.is_system_role && editingRole?.name === 'Admin')) ? 'not-allowed' : 'pointer',
                              fontSize: '0.875rem',
                              padding: '0.25rem'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={hasCapability(resource, action)}
                              onChange={() => toggleCapability(resource, action)}
                              disabled={editingRole?.viewOnly || (editingRole?.is_system_role && editingRole?.name === 'Admin')}
                              style={{ cursor: (editingRole?.viewOnly || (editingRole?.is_system_role && editingRole?.name === 'Admin')) ? 'not-allowed' : 'pointer' }}
                            />
                            {ACTION_LABELS[action]}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-light)' }}>
                  Select at least one capability for this role
                </small>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeRoleModal} disabled={submitting}>
                  {editingRole?.viewOnly ? 'Close' : 'Cancel'}
                </button>
                {!editingRole?.viewOnly && !(editingRole?.is_system_role && editingRole?.name === 'Admin') && (
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Saving...' : (editingRole ? 'Update' : 'Create')}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Role Modal */}
      {showDeleteRoleModal && editingRole && (
        <div className="modal-overlay" onClick={closeDeleteRoleModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>Delete Role</h2>

            {editingRole.is_system_role ? (
              <div className="alert alert-danger">
                <strong>Cannot Delete:</strong> System roles are protected and cannot be deleted.
              </div>
            ) : (
              <>
                <p style={{ marginBottom: '1rem' }}>
                  Are you sure you want to delete the role <strong>{editingRole.name}</strong>?
                </p>

                <div style={{
                  background: 'var(--background)',
                  padding: '1rem',
                  borderRadius: '0.375rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <strong>Role Details:</strong>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                    <div>Name: {editingRole.name}</div>
                    <div>Description: {editingRole.description || 'None'}</div>
                    <div>Capabilities: {editingRole.capabilities?.length || 0} permissions</div>
                  </div>
                </div>

                <div className="alert alert-warning">
                  <strong>Warning:</strong> This role cannot be deleted if it is currently assigned to any users.
                  Unassign all users from this role first.
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              {!editingRole.is_system_role && (
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteRole}
                  disabled={submitting}
                >
                  {submitting ? 'Deleting...' : 'Delete Role'}
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={closeDeleteRoleModal}
                disabled={submitting}
              >
                {editingRole.is_system_role ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
